import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  FolderIcon,
  UserIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BellIcon,
  CurrencyDollarIcon,
  StarIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Sidebar = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Role-based navigation items
  const getNavigationItems = () => {
    const userRole = user?.Role?.name || 'employee';
    
    const allItems = [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, roles: ['super_admin', 'admin', 'hr', 'manager', 'finance', 'employee'] },
      { name: 'Employees', href: '/employees', icon: UsersIcon, roles: ['super_admin', 'admin', 'hr'] },
      { name: 'Users', href: '/admin/users', icon: UsersIcon, roles: ['super_admin', 'admin'] },
      { name: 'Departments', href: '/departments', icon: BuildingOfficeIcon, roles: ['super_admin', 'admin', 'hr'] },
      { name: 'Leave Management', href: '/leave', icon: CalendarIcon, roles: ['super_admin', 'admin', 'hr', 'manager'] },
      { name: 'My Leave', href: '/leave', icon: CalendarIcon, roles: ['employee'] },
      { name: 'Documents', href: '/documents', icon: FolderIcon, roles: ['super_admin', 'admin', 'hr', 'manager', 'employee'] },
      { name: 'Doc Categories', href: '/documents/categories', icon: FolderIcon, roles: ['super_admin', 'admin', 'hr'] },
      { name: 'Self Service', href: '/self-service', icon: UserIcon, roles: ['employee'] },
      { name: 'Requests', href: '/requests', icon: DocumentTextIcon, roles: ['super_admin', 'admin', 'hr', 'manager', 'finance', 'employee'] },
      { name: 'Tasks', href: '/tasks', icon: DocumentTextIcon, roles: ['super_admin', 'admin', 'hr', 'manager', 'employee'] },
      { name: 'Projects', href: '/projects', icon: DocumentTextIcon, roles: ['super_admin', 'admin', 'manager'] },
      { name: 'Request Admin', href: '/requests/admin', icon: DocumentTextIcon, roles: ['super_admin', 'admin', 'hr'] },
      { name: 'Reports', href: '/reports', icon: ChartBarIcon, roles: ['super_admin', 'admin', 'hr', 'manager', 'finance'] },
      { name: 'Notifications', href: '/notifications', icon: BellIcon, roles: ['super_admin', 'admin', 'hr', 'manager', 'finance', 'employee'] },
      { name: 'Notify Prefs', href: '/notifications/preferences', icon: BellIcon, roles: ['super_admin', 'admin', 'hr', 'manager', 'finance', 'employee'] },
      { name: 'Payroll', href: '/payroll', icon: CurrencyDollarIcon, roles: ['super_admin', 'admin', 'hr', 'finance'] },
      { name: 'Performance', href: '/performance', icon: StarIcon, roles: ['super_admin', 'admin', 'hr', 'manager'] },
      { name: 'Settings', href: '/settings', icon: CogIcon, roles: ['super_admin', 'admin'] },
      { name: 'RBAC', href: '/admin/roles-permissions', icon: CogIcon, roles: ['super_admin', 'admin'] }
    ];

    return allItems.filter(item => item.roles.includes(userRole));
  };

  const navigationItems = getNavigationItems();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className={`bg-white shadow-lg border-r border-gray-200 transition-all duration-300 ${
      isCollapsed ? 'w-17' : 'w-64'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {!isCollapsed && (
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xl font-bold text-gray-900"
          >
            HR System
          </motion.h1>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          {isCollapsed ? (
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-2">
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.href;
          const Icon = item.icon;
          
          return (
            <motion.button
              key={item.name}
              onClick={() => navigate(item.href)}
              className={`w-full flex items-center px-3 py-2 rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="ml-3 text-sm font-medium"
                >
                  {item.name}
                </motion.span>
              )}
            </motion.button>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {user?.firstName?.charAt(0) || 'U'}
            </span>
          </div>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="ml-3 flex-1"
            >
              <p className="text-sm font-medium text-gray-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 capitalize">
                {user?.Role?.displayName || user?.Role?.name || 'User'}
              </p>
            </motion.div>
          )}
          <button
            onClick={handleLogout}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            title="Logout"
          >
            <XMarkIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:block relative">
        <SidebarContent />
      </div>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="fixed left-0 top-0 h-full z-50 md:hidden"
          >
            <SidebarContent />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="md:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Bars3Icon className="h-6 w-6 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">HR System</h1>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 